import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, switchMap, tap } from 'rxjs/operators';
import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, ApplicationStateChangeRequest, ApplicationType, InitiateWorkflowRequest, WorkflowData, WorkflowNote } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { Domain } from '../../../builder/models/domain.model';
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { NoteType } from '../../../common/models/note-type.enum';
import { OrganizationStub } from '../../../common/models/organization.model';
import { Well, WellType } from '../../../common/models/well.model';
import { LoaderService } from '../../../common/services/loader.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { WellService } from '../../../common/services/well.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { FormationModel } from '../../../form-controls/components/new-casing-plan/models/casing-plan.model';
import { DomainValue } from '../../../models/domain.model';
import { ApplicationConfigService, ModuleConfig } from '../../../services/application-config.service';
import { AuthService } from '../../../services/auth.service';
import { ComplianceReportingType } from '../../models/compliance.model';
import { WellSelectorComponent } from '../well-selector/well-selector.component';
import { CasingTypeModel } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { OnBaseDocumentTypeProcessReference, OnBaseSubProcessName } from '../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-final-completion-report',
    templateUrl: './final-completion-report.component.html',
    styleUrls: ['./final-completion-report.component.scss'],
    standalone: false
})
export class FinalCompletionReportComponent extends ApplicationBaseComponent implements OnInit {
    @Input() applicationState: ApplicationState;
    @Input() organization: OrganizationStub;
    @Input() logData: any;
    @Input() application: WorkflowData;
    @Input() reportType: ComplianceReportingType;
    @ViewChild(WellSelectorComponent) private _selector: WellSelectorComponent;
    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];
	_validStringCasingTypeCodes = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'PROD2', 'HOL1', 'PKR', 'L1', 'T1'];

    well: Well;
    wellIsSelected: boolean = false;

    formations: FormationModel[];
	maxFormations: number = 15;
    selectedFormations: any[] = [];
    wellTypes: any[] = [];

	dataTypes: CasingTypeModel[] = [];

	private _itemList: number[] = undefined;
	get itemList(): number[] {
		if (this._itemList == null) {
			this._itemList = [];
			for (let i = 0; i < this.maxFormations; i++) {
				this._itemList.push(i);
			}
		}
		return this._itemList;
	}


	methods: any[] = [];

    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    static saveUnsubmittedMessage: string = "Your application has been saved for completion at a later date.  For future reference, this report is stored as ID [XXX].";
    saveUnsubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { this.navigateToSavedReport(); return true; })]);
    canceledDialog: Dialog = new Dialog("Leave application unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.home();}), new DialogButton("No", true, () => { }),]);
    returnedDialog: Dialog = new Dialog("Return this Form 8 for corrections?", [new DialogButton("Confirm", true, () => { this.onReturnConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    returnedConfirmedDialog: Dialog = new Dialog("The application has been returned for corrections.", [new DialogButton("OK", true, () => { this.home(); })]);
    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);


    get disableSubmit(): boolean {
        if (this.form == null) return false;
        return this.form.invalid;
    }


    get onBehalf(): boolean {
        return this.auth.User.isInternal;
    }

    get organizationId(): string {
        if (this.form == null) return "";
        let orgForm = this.form.get('organization');
        if (orgForm == null) return "";
        let idCtrl = orgForm.get('id');
        if (idCtrl == null) return "";
        return idCtrl.value;
    }

    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('apiNumber');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

    get isApproved(): boolean {
        if (this.application == null) return null;
        if (this.application.applicationState == ApplicationState.Approved) return true;
        return false;
    }

    get isInCorrections(): boolean {
        if (this.application == null) return null;
        if (this.application.applicationState == ApplicationState.Corrections) return true;
        return false;
    }

    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });


    wellTypeDescriptionDomainValues: DomainValue[];
    wellTypeDescriptionDomainId: string;
    wellTypeDescriptionDomainValues$: Observable<any>;

    get enableWellSelection(): boolean {
        if (this.application == null) return true;
        else {
            if (this.isApproved) return false;
            if (this.isInternal && this.isInCorrections) return false;
            if (this.getFilteredNotes('apiNumber').length != 0 && this.isInCorrections) return true;
            if (this.application.applicationState == ApplicationState.Input || this.application.applicationState == ApplicationState.Start) return true;
            return false;
        }
    }

    constructor(loader: LoaderService, dialog: DynamicDialogService, subheader: SubHeaderService, auth: AuthService, public appService: ApplicationsService, private configService: ApplicationConfigService,
        router: Router, sidePanelSvc: SidePanelService, private domainService: DomainDefinitionService, noteSvc: ApplicationNotesService, private builder: FormBuilder, public wellService: WellService) {
        super(loader, dialog, subheader, auth, appService, router, sidePanelSvc, noteSvc);

        this.numericFields = ['ownerWellInfo|surfaceLat', 'ownerWellInfo|surfaceLong', 'ownerWellInfo|surfaceLong', 'wellInfo|drillerTotalDepth', 'wellInfo|loggerTotalDepth', 'wellInfo|groundLevel', 'wellInfo|derrickFloor', 'wellInfo|kellyBushing', 'completionInfo|shotLbs', 'completionInfo|shotQts', 'completionInfo|acidGals', 'completionInfo|acidPercent',
            'completionInfo|waterGals', 'completionInfo|waterBbl', 'completionInfo|fracCos', 'completionInfo|fracN2', 'completionInfo|sandLbs', 'completionInfo|sandSks', 'completionInfo|pressureBreakdown', 'completionInfo|pressureATP', 'completionInfo|pressureISIP', 'completionInfo|pressureSIP', 'completionInfo|pressureAvg', 'initialProd|naturalGas', 'initialProd|naturalOil', 'initialProd|naturalOil', 'initialProd|afterTreatGas', 'initialProd|afterTreatOil', 'initialProd|afterTreatBrine', 'initialProd|sercTanks', 'initialProd|sercStorage']
        this.trueBooleanFields = ['ownerWellInfo|wellNotDrilled', 'completionInfo|openHole', 'completionInfo|slottedLiner', 'completionInfo|thruCasing', 'completionInfo|swabPit', 'completionInfo|swabFrac', 'completionInfo|flowPit', 'completionInfo|flowFrac',]
        this.fileFields = ['documentation|cementTickets', 'documentation|cementJobLogs', 'documentation|safetyDateSheet', 'documentation|cementData', 'documentation|prGraph', 'documentation|stimInvoice', 'documentation|stimRecord', 'documentation|stimLog']
        this.arrayFields = ['formations|selectedFormations']
        this.dateFields = ['wellInfo|drillingStartDate', 'wellInfo|drillingEndDate', 'completionInfo|productionDate', 'completionInfo|dateTreated', 'receivedDates|formReceivedDate', 'receivedDates|cementTickReceivedDate', 'receivedDates|cementLogReceivedDate', 'receivedDates|safetySheetReceivedDate', 'receivedDates|prGraphReceivedDate', 'receivedDates|stimInvoiceDateReceived', 'receivedDates|stimRecordReceivedDate','receivedDates|stimLogReceivedDate',]
    }

    ngOnInit() {
        this.form = this.buildForm();
        this.application = this.application

        let obs$: Observable<FormationModel[]> = this.domainService.getFormations().pipe(
            tap((models: FormationModel[]) => {
                this.formations = models;
            })

        );

        let subs$: Subscription = obs$.subscribe()
        this.subscription.add(subs$);

        const type$ = this.domainService.getValues("WELL_TYP").pipe(
            catchError((error) => {
                console.error(error);
                return of([]);
            })
        );
        const wlType$ = this.domainService.getValues("WELL_TYPE").pipe(
            catchError((error) => {
                console.error(error);
                return of([]);
            })
		);

		const casingString$: Observable<any> = this.domainService.getValues("CSG_STRING").pipe(
			tap((casings: any[]) => {
				if (casings != null) {
					let unsorted: CasingTypeModel[] = [];
					casings.forEach(c => {
						if (this._validStringCasingTypeCodes.includes(c.Code)) {
							const model = new CasingTypeModel();
							model.code = c.Code;
							model.definition = (c.Code !== 'HOL1') ? c.Definition : 'Open Hole';
							model.isActive = c.isActive;
							model.isRequired = c.isRequired;
							model.lastModifiedDateUTC = c.LastModifiedDateUTC;
							unsorted.push(model);
						}
					});
					this._validStringCasingTypeCodes.forEach(code => {
						let model = unsorted.find(m => m.code === code);
						if (model != null) {
							this.dataTypes.push(model);
						}
					})
				}
				this.form = this.buildForm();
			})
		);
		const method$: Observable<any> = this.domainService.getValues("METH_OBTND").pipe(
			tap((data: any[]) => {
				this.methods = data;
			})
		);
        const allTypes$ = combineLatest(type$, wlType$).pipe(
            tap((domains: Domain[][]) => {
                domains.forEach(domain => {
                    domain.forEach(d => {
                        if (this.wellTypes.findIndex(w => w.Code === d.Code) === -1) {
                            this.wellTypes.push(d);
                        }
                    })
                });
                this.wellTypes = this.wellTypes.sort((a, b) => {
                    if (a.Definition > b.Definition) return 1;
                    if (a.Definition < b.Definition) return -1;
                    return 0;
                });
            })
        );
		let conf$: Observable<ModuleConfig> = this.configService.getModuleConfig().pipe(
			tap((config: ModuleConfig) => {
				if (config.postDrilling != null) {
					this._validStringCasingTypeCodes = config.postDrilling.validCasingTypes;
					this.maxFormations = config.postDrilling.maximumFormations;
				}
			}),
			switchMap(() => {
				return casingString$;
			})
		);

		let typeSub$: Subscription = forkJoin(allTypes$, conf$, method$).subscribe(
			() => {
				if (this.application != null) {
					this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
					if (this.application.applicationState != ApplicationState.Approved && this.isInternal) {
						this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });

					}
					this.subheaderService.updateSubHeader(this.subheader);
					this.form = this.restoreFormData(this.form);
					if (this._selector != null) {
						this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
						this._selector.findWells();

					}
					else {
						console.error("no selector component ready");
					}
				}
			},
			(error) => {
				console.error(error);
				this.dialogService.showError();
			}

		)
		this.subscription.add(typeSub$);




	}

    ngAfterViewInit() {

    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            wellName: ["", []],
            wellNumber: [null, []],
            apiNumber: ["", []],
            organizationName: [this.organization.name, []],
            organization: [this.organization.id, []],
            rbdmsNumber: [this.organization.historicalOwnerNumber, []],
            reportType: [this.reportType.typeId, []],
            selectedWell: [null, []],
            ownerWellInfo: this.builder.group({
                ownerNumber: [{ value: null, disabled: true }, []],
                ownerName: [{ value: null, disabled: true }, []],
                apiNumber: [{ value: null, disabled: true }, []],
                wellName: [{ value: null, disabled: true }, []],
                wellNumber: [{ value: null, disabled: true }, , []],
                surfaceLat: [{ value: null, disabled: true }, []],
                surfaceLong: [{ value: null, disabled: true }, []],
                county: [{ value: null, disabled: true }, []],
                township: [{ value: null, disabled: true }, []],
                quadrangle: [{ value: null, disabled: true }, []],
                section: [{ value: null, disabled: true }, []],
                lot: [{ value: null, disabled: true }, []],
                tract: [{ value: null, disabled: true }, []],
                typeOfWell: [{ value: null, disabled: true }, []],
                wellNotDrilled: [{ value: null, disabled: false }, []],
            }),
            wellInfo: this.builder.group({
                drillingStartDate: [null, []],
                drillingEndDate: [null, []],
                deepestFormation: [null, []],
                drillerTotalDepth: [null, []],
                loggerTotalDepth: [null, []],
                drillingContractor: [null, []],
                groundLevel: [null, []],
                derrickFloor: [null, []],
                kellyBushing: [null, []]
            }),
            documentation: this.builder.group({
                cementTickets: [[], []],
                cementJobLogs: [[], []],
                safetyDateSheet: [[], []],
                cementData: [[], []],
                prGraph: [[], []],
                stimInvoice: [[], []],
                stimRecord: [[], []],
                stimLog: [[], []],

            }),
			formations: this.buildFormationsForm(),
            casingTubing: this.buildCasingTubingForm(),
            userUpdates: this.builder.group({
                disposition: [null, []],
                category: [null, []],
                wellSymbol: [null, []],
                wellTypeDesc: [null, []],
                legacyTracking: [null, []]
            }),
            completionInfo: this.builder.group({
                productionDate: [null, []],
                prodFormation: [null, []],
                fracCompany: [null, []],
                intervalsAndShots: [null, []],
                openHole: [null, []],
                thruCasing: [null, []],
                slottedLiner: [null, []],
                shotLbs: [null, []],
                shotQts: [null, []],
                shotType: [null, []],
                acidGals: [null, []],
                acidType: [null, []],
                acidPercent: [null, []],
                waterGals: [null, []],
                waterBbl: [null, []],
                fracCos: [null, []],
                fracN2: [null, []],
                sandLbs: [null, []],
                sandSks: [null, []],
                pressureBreakdown: [null, []],
                pressureATP: [null, []],
                pressureISIP: [null, []],
                pressureSIP: [null, []],
                pressureAvg: [null, []],
                swabPit: [null, []],
                swabFrac: [null, []],
                flowPit: [null, []],
                flowFrac: [null, []],
                dateTreated: [null, []]
            }),
            receivedDates: this.builder.group({
                formReceivedDate: [null, []],
                cementTickReceivedDate: [null, []],
                cementLogReceivedDate: [null, []],
                safetySheetReceivedDate: [null, []],
                prGraphReceivedDate: [null, []],
                stimInvoiceDateReceived: [null, []],
                stimRecordReceivedDate: [null, []],
                stimLogReceivedDate: [null, []],
            }),
            initialProd: this.builder.group({
                naturalGas: [null, []],
                naturalOil: [null, []],
                naturalBrine: [null, []],
                afterTreatGas: [null, []],
                afterTreatOil: [null, []],
                afterBrine: [null, []],
                sercTanks: [null, []],
                sercStorage: [null, []],
            }),
            certifications: this.builder.group({
                submitterName: [null, [Validators.required]],
                submitterTitle: [null, [Validators.required]]
            })



        });
    }

    buildCasingTubingForm(): FormGroup {
        let obj: any = {};
        this.dataTypes.forEach((type) => {
			obj[type.code] = this.buildSubCasingTubingForm();
			this.numericFields.push(`casingTubing|${type.code}|wellboreDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|casingDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|collarDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|feetInstalled`);
			this.numericFields.push(`casingTubing|${type.code}|feetLeftInWell`);
			this.numericFields.push(`casingTubing|${type.code}|cementVolume`);
			this.numericFields.push(`casingTubing|${type.code}|cementTop`);
			this.numericFields.push(`casingTubing|${type.code}|grade`);
			this.numericFields.push(`casingTubing|${type.code}|weight`);
			this.numericFields.push(`casingTubing|${type.code}|bottom`);
			this.numericFields.push(`casingTubing|${type.code}|burstPressureRating`);

        });
        return this.builder.group(obj);
    }

    buildSubCasingTubingForm(): FormGroup {
        let subObj: any = {};
        subObj["wellboreDiameter"] = [{ value: null, disabled: false }, []];
        subObj["casingDiameter"] = [{ value: null, disabled: false }, []];
        subObj["collarDiameter"] = [{ value: null, disabled: false }, []];
        subObj["feetInstalled"] = [{ value: null, disabled: false }, []];
        subObj["feetLeftInWell"] = [{ value: null, disabled: false }, []];
        subObj["cementVolume"] = [{ value: null, disabled: false }, []];
        subObj["cementTop"] = [{ value: null, disabled: false }, []];
        subObj["grade"] = [{ value: null, disabled: false }, []];
        subObj["weight"] = [{ value: null, disabled: false }, []];
		subObj["burstPressureRating"] = [{ value: null, disabled: false }, []];
		subObj["bottom"] = [{ value: null, disabled: false }, []];
        subObj["toolType"] = [{ value: null, disabled: false }, []];

        return this.builder.group(subObj);
	}

	buildFormationsForm(): FormGroup {
		let formationForm: any = {};
		for (let f = 0; f < this.maxFormations; f++) {
			formationForm[`selectedFormation${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`top${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`base${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`showsOf${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`methodObtained${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`producingFormation${f}`] = [{ value: false, disabled: false }, []];
			formationForm[`remarks${f}`] = [{ value: null, disabled: false }, []];
			formationForm[`aapgCode${f}`] = [{ value: null, disabled: false }, []];
			this.trueBooleanFields.push(`formations|producingFormation${f}`);
			this.numericFields.push(`formations|top${f}`);
			this.numericFields.push(`formations|base${f}`);
		}
		return this.builder.group(formationForm);
	}

    restoreWells(): void {
        if (this.application != null) {
            if (this._selector != null) {
                this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
                this._selector.findWells();
            }
            else {
                console.error("no selector component ready");
            }
        }
    }


    onWellsSelected(evt: Well[]): void {
        let selectedWell = this.form.get('apiNumber');
        if (evt.length != 0) {

            let wellName: string = evt[0].name;
            if (evt[0].lease != null && evt[0].lease.name != null && evt[0].lease.name.length > 0) wellName = evt[0].lease.name;
            else if (evt[0].wellNumber != null && evt[0].wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(evt[0].wellNumber.trim())) {
                wellName = wellName.trim().replace(evt[0].wellNumber.trim(), "");
            }

            let latitude: number = 0;
            let longitude: number = 0;

            if (evt[0].location.gisPoints[0].coordinates.latitude83 != null) {
                latitude = evt[0].location.gisPoints[0].coordinates.latitude83;

            }
            else {
                latitude = evt[0].location.gisPoints[0].coordinates.latitude;
            }

            if (evt[0].location.gisPoints[0].coordinates.longitude83 != null) {
                longitude = evt[0].location.gisPoints[0].coordinates.longitude83;

            }
            else {
                longitude = evt[0].location.gisPoints[0].coordinates.longitude;
            }

            let quadrangle: string = `${evt[0].location.gisPoints[0].ogsQuadrangle.name}` + ' ' + `${evt[0].location.gisPoints[0].ogsQuadrangle.code}`;

            if (evt[0].wellStatus && evt[0].wellStatus.type) {
                switch (evt[0].wellStatus.type.type) {
                    case "OG_R":
                        this.wellTypeDescriptionDomainId = "OilAndGasWellTypeDescriptions";
                        break;
                    case "ER_R":
                        this.wellTypeDescriptionDomainId = "EnhancedRecoveryWellTypeDescriptions";
                        break;
                    case "SW_R":
                        this.wellTypeDescriptionDomainId = "DisposalWellTypeDescriptions";
                        break;
                    case "UN_R":
                        this.wellTypeDescriptionDomainId = "UnknownWellTypeDescriptions";
                        break;
                }
            }

            this.wellTypeDescriptionDomainValues$ = this.domainService.getValues(this.wellTypeDescriptionDomainId);

            if (this.wellTypeDescriptionDomainId != null)
                this.loadWellTypeDescriptionData();

            
            let typeObj: WellType = evt[0].wellStatus.type;
            let type: string = "";
            if (this.wellTypes != null && this.wellTypes.find(f => f.Code == typeObj.type) != null) {
                type = this.wellTypes.find(f => f.Code == typeObj.type).Definition;
            }



            this.form.get('wellNumber').setValue(evt[0].wellNumber);
            this.form.get('wellName').setValue(wellName);
            this.form.get("ownerWellInfo").get("ownerNumber").setValue(evt[0].organizationId);
            this.form.get("ownerWellInfo").get("ownerName").setValue(evt[0].organizationName);
            this.form.get("ownerWellInfo").get("apiNumber").setValue(evt[0].id);
            this.form.get("ownerWellInfo").get("wellName").setValue(wellName);
            this.form.get("ownerWellInfo").get("wellNumber").setValue(evt[0].wellNumber);

            this.form.get("ownerWellInfo").get("surfaceLat").setValue(latitude);
            this.form.get("ownerWellInfo").get("surfaceLong").setValue(longitude);
            this.form.get("ownerWellInfo").get("county").setValue(evt[0].location.gisPoints[0].county.name);
            this.form.get("ownerWellInfo").get("township").setValue(evt[0].location.gisPoints[0].township.name);
            this.form.get("ownerWellInfo").get("quadrangle").setValue(quadrangle);
            this.form.get("ownerWellInfo").get("section").setValue(evt[0].location.gisPoints[0].section);
            this.form.get("ownerWellInfo").get("lot").setValue(evt[0].location.gisPoints[0].lot);
            this.form.get("ownerWellInfo").get("tract").setValue(evt[0].location.gisPoints[0].tract);
            this.form.get("ownerWellInfo").get("typeOfWell").setValue(type);

        }

        let update: boolean = false;
        evt.forEach(w => {
            if (w.id != selectedWell.value) {
                this.form.get('apiNumber').setValue(w.id);

                update = true;
            }
        });
        if (update) {
            if (evt.length == 0) {
                this.form.get('apiNumber').setValue(null);

            }
        }


        this.well = evt[0];

        if (selectedWell != null) {
            this.wellIsSelected = true;

        }

    }


    cancel() {

        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }



    onAccept() {
        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed() {



        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.Approved;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Approved;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.acceptConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.acceptConfirmedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }


    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }


    buildRequest(behalfFlag: boolean): InitiateWorkflowRequest {
        return {
            organizationId: this.organization.id,
            applicationType: ApplicationType.ComplianceReporting,
            onBehalf: behalfFlag,
            reportType: "F8F"

        } as InitiateWorkflowRequest
    }

    save(): void {
		let obs$: Observable<WorkflowData>
		if (this.application == null) {
			obs$ = this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
				this.application = app;
				return this.saveProgress();
			}));
		} else {
			obs$ = this.saveProgress();
		}
        this._loader.toggleLoader(true);

        let subs$ = obs$.pipe()
            .subscribe(
                (app) => {
                    this._loader.toggleLoader(false);
                    this.logData.id = app.applicationNumber;
                    const dialog: Dialog = this.saveUnsubmittedDialog;

                    dialog.message = FinalCompletionReportComponent.saveUnsubmittedMessage.replace("[XXX]", app.applicationNumber);
                    dialog.toggleDialog(true);
                    this.dialogService.updateDialog(dialog);
                    this.form.markAsPristine();
					this.restoreWells();
                    this.allowDeactivate.next(true);
                },
                (error) => {
					console.error(error);
					this.dialogService.showError();
					this._loader.toggleLoader(false);
                }
            );

        this.subscription.add(subs$);
    }

    submit(): void {
        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.InitialReview;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.InitialReview;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.submittedDialog.message = this.submittedDialog.message.replace('#[APPNUMBER]', app.applicationNumber);
                    this._loader.toggleLoader(false);
                    this.submittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.submittedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }


    onReturn() {
        this.returnedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.returnedDialog);

    }

    onReturnConfirmed() {

        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Corrections;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.returnedConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.returnedConfirmedDialog);


                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$)

    }


    navigateToSavedReport(): void {
        this.router.navigate([`/issues/${this.logData.id}/resume`]);

    }

    loadWellTypeDescriptionData() {
        //Fetch the well type description domain values, if applicable
        if (this.wellTypeDescriptionDomainId != null) {
            this.subscription.add(this.wellTypeDescriptionDomainValues$.pipe(
            ).subscribe(
                (domainValues: DomainValue[]) => {
                    if (domainValues)
                        this.wellTypeDescriptionDomainValues = domainValues;
                },
                error => { console.error(error); console.error("ERROR Fetching the " + this.wellTypeDescriptionDomainId + " domain for this well."); }
            ));
        }
    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }
  

}
